import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	PLATFORM_ID,
	Renderer2,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { Language, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { map, Observable, switchMap } from 'rxjs';

import {
	CarouselComponent,
	ContentMargin,
} from '@valk-nx/components/ui-carousel/src/lib/carousel';
import { CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { ReadMoreComponent } from '@valk-nx/components/ui-read-more/src/lib/read-more';
import { RoomFacilitiesComponent } from '@valk-nx/components/ui-room-facilities/src/lib/room-facilities.component';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	fallbackLanguage,
	HOTEL_SLUG,
	LANGUAGE,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { AccommodationTypeModule } from '@valk-nx/core/lib/pipes/accommodation-type/accommodation-type.module';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { PriceHelper } from '@valk-nx/helpers/lib/price/price.helper';
import { PricingTooltipOccupancy } from '@valk-nx/helpers/lib/pricing-tooltip/pricing-tooltip.interface';
import { AvailabilityWidgetComponent } from '@valk-nx/storyblok/components/availability-widget/src/lib/availability-widget';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { RoomData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-room-detail',
	templateUrl: './room-detail.component.html',
	imports: [
		AccommodationTypeModule,
		AvailabilityWidgetComponent,
		CarouselComponent,
		CommonModule,
		ListGridComponent,
		MarkdownModule,
		MapComponent,
		PersuadeCardComponent,
		ReadMoreComponent,
		RoomFacilitiesComponent,
		MiniCarouselComponent,
		MarkdownModule,
		RatingComponent,
		RouterModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TagComponent,
		TranslatePipe,
	],
})
export class RoomDetailComponent extends StoryblokRootDirective {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly renderer2 = inject(Renderer2);
	private readonly translate = inject(TranslateService);
	readonly platformId = inject(PLATFORM_ID);
	readonly hotelSlug = inject(HOTEL_SLUG);
	readonly bookingtool = inject(BOOKINGTOOL);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	readonly currentLanguage = signal<string>(fallbackLanguage);
	readonly language: string = this.rootLanguage;

	readonly hotelStore = inject(HotelStore);

	roomSlug = '';
	isSmallTablet$: Observable<boolean>;
	roomData$: Observable<
		RoomData & { pricingToolTipOccupancy: PricingTooltipOccupancy }
	>;
	images: CarouselItem[] = [];
	hotelImages: ImageInterface[] = [];
	roomFacilities: string[] = [];
	disabledReadmore = true;

	// Enum for template use
	ContentMargin = ContentMargin;

	constructor() {
		super();
		negativeHeader$.next(true);

		this.currentLanguage.set(
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang(),
		);

		this.roomData$ = this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				return (
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0]
				);
			}),
			switchMap((slug) => {
				this.roomSlug = slug.path.split('/').pop();
				return this.contentService.getRoom(
					this.roomSlug,
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => {
				const roomData = {
					...response.data,
					pricingToolTipOccupancy: PriceHelper.getPricingTooltip(
						response.data.occupancyRestrictions,
					),
					bookingToolRedirectUrl:
						BookingRedirectHelper.getRedirectUrl(
							this.bookingtool,
							this.hotelSlug,
							{
								roomGuid: response.data.guid,
								languageCode: this.currentLanguage(),
							},
						),
				};
				this.metadataService.updateMetaDataBatch(
					roomData,
					this.robotsNoFollow,
					this.robotsNoIndex,
				);
				this.metadataService.setSchemaOrgRoomDetail(
					this.renderer2,
					roomData,
					this.hotelStore.hotelMainContact(),
				);

				this.images = roomData.images.map(
					(image: { src: string; alt: string }) => {
						return {
							image: {
								src: image.src,
								alt: image.alt,
							},
							heading: null,
							subHeading: null,
							link: null,
						};
					},
				);

				const { misc, generic, sleepingComfort, bathroom } =
					roomData.facilities;
				const optionalGeneric = generic ?? [];
				const facilities = [
					...sleepingComfort,
					...bathroom,
					...misc,
					...optionalGeneric,
				];
				this.roomFacilities = [...new Set(facilities)];

				/* istanbul ignore else */
				if (this.images.length === 0) {
					negativeHeader$.next(false);
				} else {
					negativeHeader$.next(true);
				}

				this.hotelImages = roomData.hotel?.images.map(
					(image: { src: string; alt: string }) => {
						return {
							src: image.src,
							altText: image.alt,
							width: 0,
							height: 0,
						};
					},
				);

				return roomData;
			}),
		);
	}
}
